import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Label, Text } from '@mercai/clever'

import { getProfileList } from '@/services'

import styles from './search-clients-step.module.css'

import {
  ProfileDataProps,
  SearchClientsStepProps,
} from './search-clients-step.type'

import { ProfileButton, TextAreaSearch } from './components'

// Função para validar email
const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(value)
}

// Função para validar telefone (aceita vários formatos)
const isPhone = (value: string): boolean => {
  const phoneRegex = /^[\d\s()+-]+$/
  const cleanValue = value.replace(/\s/g, '')
  return phoneRegex.test(value) && cleanValue.length >= 8
}

export const SearchClientsStep = ({
  onSelectedLeads,
  selectedLeads,
  errors,
}: SearchClientsStepProps) => {
  const { t } = useTranslation('screenAiCenter')

  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const onHandleChangeSearch = async (newValue: string) => {
    setIsLoading(true)

    const hasNewLine = newValue.includes('\n')
    const hasComma = newValue.includes(',')

    if (hasNewLine && hasComma) {
      const lines = newValue
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => !!line)

      const mappedProfiles = lines.map((line) => {
        const fields = line
          .split(',')
          .map((field) => field.trim())
          .filter((field) => !!field)

        const profile: ProfileDataProps = {
          uuid: '',
          name: '',
          email: '',
          phone: '',
        }

        fields.forEach((field) => {
          if (isEmail(field)) {
            profile.email = field
          } else if (isPhone(field)) {
            profile.phone = field
          } else {
            profile.name = field
          }
        })

        return profile
      })

      const filteredProfiles = mappedProfiles.filter(
        (profile) => !selectedLeads.some((lead) => lead.uuid === profile.uuid),
      )

      onSelectedLeads([...selectedLeads, ...filteredProfiles])
    } else {
      const separator = hasNewLine ? '\n' : ','
      const splitedValue = newValue
        .split(separator)
        .map((item) => item.trim())
        .filter((item) => !!item)

      const requests = splitedValue.map((item) => {
        let searchParam = item

        if (isEmail(item)) {
          searchParam = item
        } else if (isPhone(item)) {
          searchParam = item
        } else {
          searchParam = item
        }

        return getProfileList({ params: { search: searchParam } })
      })

      const profiles = await Promise.all(requests)

      const mappedProfiles = profiles.map((profile) => {
        const profileObject = profile.data?.results?.[0]

        return {
          uuid: profileObject?.uuid || '',
          name: profileObject?.name || profileObject?.companyName || '',
          email: profileObject?.email || '',
          phone: profileObject?.phone || '',
        }
      })

      const filteredProfiles = mappedProfiles.filter(
        (profile) => !selectedLeads.some((lead) => lead.uuid === profile.uuid),
      )

      onSelectedLeads([...selectedLeads, ...filteredProfiles])
    }

    setSearch(newValue)

    setIsLoading(false)
  }

  const onHandleRemoveLead = (index: number) => {
    onSelectedLeads(selectedLeads.filter((_, i) => i !== index))
  }

  const onHandleChangeLead = (index: number, data: ProfileDataProps) => {
    const copySelectedLeads = JSON.parse(JSON.stringify(selectedLeads))
    copySelectedLeads[index] = data
    onSelectedLeads(copySelectedLeads)
  }

  return (
    <div className={styles.core}>
      <div className={styles['search-container']}>
        <div>
          <Label>{t('conversationNewSearchLeadsLabel')}</Label>

          <TextAreaSearch value={search} onChange={onHandleChangeSearch} />
        </div>

        <div className={styles['leads-container']}>
          {selectedLeads?.length > 0 &&
            selectedLeads?.map((lead, index) => (
              <ProfileButton
                key={`${lead?.email}_${index}`}
                index={index}
                data={lead}
                isLoading={isLoading}
                onClick={() => onHandleRemoveLead(index)}
                onHandleChange={(newValue) =>
                  onHandleChangeLead(index, newValue)
                }
                errors={errors}
              />
            ))}

          {isLoading &&
            [0, 1, 2].map((item) => (
              <ProfileButton
                key={item}
                isLoading={true}
                data={{} as ProfileDataProps}
                onHandleChange={() => {}}
                index={0}
                errors={errors}
              />
            ))}

          {errors?.selectedLeads && (
            <Text className={styles['error-message']}>
              {errors?.selectedLeads as string}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
